import axios from 'axios';

import { API, apiOrigin, isPublicRoute, ROUTES, STORAGE_KEYS } from '../config';
import { notify } from './notifications';
import { formatApiError } from './errors';
import { canonicalizeLoginIdentifier } from './loginIdentifier';
import { isAuthHandshakeUrl } from './authHandshake';
import { clearLocalAuth, isSignedOut, writeTokens } from './authStorage';
import { redirectToLogin } from './sessionRedirect';


/**
 * Pre-configured axios instance.
 *
 * - `baseURL` comes from `config.js` (env-aware).
 * - We attach the JWT access token (if any) on every request.
 * - We also send the Django session cookie via `withCredentials: true` so
 *   browsers that are already logged into the HTML site keep working.
 * - On `401` we try a single silent refresh using the refresh token.
 */
function apiRoot() {
  return apiOrigin() || API.BASE_URL || '';
}

const api = axios.create({
  baseURL: apiRoot() || undefined,
  timeout: API.TIMEOUT_MS,
  withCredentials: true,
  headers: { Accept: 'application/json' },

  // ----------------------------------------------------------------
  // Django CSRF compatibility.
  //
  // Why this matters
  // ----------------
  // When the user has no usable JWT in localStorage (expired access
  // token, or they signed in via the legacy HTML ``/super_admin/login``
  // form which only sets the session cookie) DRF's auth chain falls
  // through from ``TenantAwareJWTAuthentication`` to
  // ``SessionAuthentication``.  Session auth enforces CSRF on every
  // unsafe method (POST/PUT/PATCH/DELETE) by comparing the
  // ``X-CSRFToken`` request header against the ``csrftoken`` cookie.
  //
  // Axios's defaults follow the Angular convention
  // (``XSRF-TOKEN`` cookie → ``X-XSRF-TOKEN`` header), which Django
  // does NOT recognise.  Without these overrides every super-admin
  // form submission (e.g. "Create User" at
  // ``/super-admin/users/new``) is rejected with a CSRF 403, which
  // the response interceptor below was then turning into a misleading
  // ``/access-denied?reason=unauthorized`` redirect.
  //
  // Setting both names to Django's expectation makes form POSTs work
  // for the session-auth path and is a no-op for the JWT path
  // (Bearer-auth requests are explicitly CSRF-exempt in DRF).
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// --- request interceptor: attach Bearer token ---
api.interceptors.request.use((config) => {
  if (isSignedOut()) {
    if (config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  }
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- response interceptor: refresh token on 401 (one retry only) ---
//
// We *also* surface every other failure as a toast via the global notify
// facade.  The user's brief: "any and all errors that are there in the
// backend should be displayed in the frontend as well, so that we can
// look into it." — so even when a calling page does `.catch(() => {})`
// and swallows the error, the toaster still shows it.
//
// The interceptor still re-rejects so existing per-page `.catch` handlers
// keep getting the error object (no behaviour change for happy-path code).
let refreshPromise = null;

/**
 * Decide whether a given error should produce a global toast.
 *
 * Skipped:
 *   - `error.config.silent === true` — opt-out for callers that prefer to
 *     handle errors completely on their own (e.g. polling calls).
 *   - 401s that we are about to retry via refresh — they're not real errors
 *     from the user's POV.
 *   - 401s on the bootstrap `/me/` call — being unauthenticated at app boot
 *     is the *expected* path that sends you to the login page; it would be
 *     extremely noisy to toast it.
 */
function shouldToast(error) {
  const cfg    = error.config || {};
  const status = error.response && error.response.status;
  const url    = (cfg.url || '').toString();

  if (cfg.silent) return false;

  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return false;

  // Auth endpoints render their failures inline on the LoginPage
  // (`<ErrorBox/>`), so toasting them too would be a noisy duplicate.
  // Refresh-token failures are toasted by the dedicated `Session expired`
  // path below — not from here.
  if (url.includes(API.ENDPOINTS.LOGIN))         return false;
  if (url.includes(API.ENDPOINTS.PUBLIC_SIGNUP)) return false;
  if (url.includes(API.ENDPOINTS.PASSWORD_RESET)) return false;
  if (url.includes(API.ENDPOINTS.TOKEN))         return false;
  if (url.includes(API.ENDPOINTS.LOGOUT))        return false;
  if (url.includes(API.ENDPOINTS.TOKEN_REFRESH)) return false;

  if (status === 401) {
    // Bootstrap "who am I?" probe — don't toast.
    if (url.includes(API.ENDPOINTS.ME))            return false;
    // For other 401s we only toast AFTER a refresh attempt has been tried.
    if (!cfg.__isRetry && localStorage.getItem(STORAGE_KEYS.REFRESH)) {
      return false;
    }
  }

  // 403 (forbidden) — handled by the dedicated redirect-to-AccessDenied
  // path below; never toast.  Showing a red ⚠ banner on top of the
  // friendly page would be redundant and confusing.
  if (status === 403) return false;


  // 404 on a tenant-only endpoint while we're on the public host — same
  // story.  This is the original symptom the multi-tenant brief asked
  // us to fix: the route guard now hides those pages, but if anything
  // slips through we don't want to scream about it.
  if (status === 404) {
    if (url.includes('/api/task/'))       return false;
    if (url.includes('/api/complaint'))   return false;
    if (url.includes('/api/chat/'))       return false;
    if (url.includes('/api/admin/'))      return false;
  }

  return true;
}

/**
 * Send the user to the shared ``AccessDenied`` page after a 403.
 *
 * Why a hard ``window.location`` instead of ``react-router``'s
 * ``navigate``?  This file is a plain module — it has no access to
 * the Router context, and writing a hook-based bridge for one redirect
 * isn't worth the complexity.  ``location.assign`` keeps the broken
 * URL in browser history so the user can use "Back" to retry once
 * permissions change.
 *
 * The skip guard prevents an infinite loop in the (unlikely) event that
 * the AccessDenied page itself triggers a 403 — which can only happen
 * if a future addition wires an API call into it.
 */
function redirectToAccessDenied(reason = 'unauthorized') {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (path.startsWith(ROUTES.ACCESS_DENIED)) return;
  if (path.startsWith(ROUTES.LOGIN))         return;
  // Public pages render their own 403 explanation in-page.  The Uni Forms
  // fill page is the reason this guard exists: an internal or restricted
  // form answers 403 with a ``state`` the page turns into "sign in to
  // continue" / "you're not on this form's audience" — far more useful to
  // a respondent than a generic "Access denied" screen, and it keeps the
  // form's title on screen so they know which link failed.
  if (isPublicRoute(path))                   return;
  const target = `${ROUTES.ACCESS_DENIED}?reason=${encodeURIComponent(reason)}`;
  window.location.assign(target);
}



api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response && error.response.status;
    const url = (original.url || '').toString();
    const handshake = isAuthHandshakeUrl(url);
    const refresh = isSignedOut() ? null : localStorage.getItem(STORAGE_KEYS.REFRESH);

    if (status === 401 && refresh && !original.__isRetry && !isSignedOut()) {
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(
              (apiRoot() || '') + API.ENDPOINTS.TOKEN_REFRESH,
              { refresh },
              { withCredentials: true },
            )
            .then((r) => {
              if (isSignedOut()) {
                throw new Error('signed out during refresh');
              }
              writeTokens({
                access: r.data && r.data.access,
                refresh: r.data && r.data.refresh,
              });
              return r.data.access;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }
        const newAccess = await refreshPromise;
        original.__isRetry = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (e) {
        // refresh failed — wipe creds, then bounce the user to /login
        // so they don't land on a half-broken page that keeps firing
        // 401s.  Handshake probes (/me, logout, login) must not bounce
        // or they close a /login ↔ /task loop.
        clearLocalAuth();
        if (shouldToast(e)) {
          notify.error(formatApiError(e), { title: 'Session expired' });
        }
        if (!handshake) redirectToLogin();
      }
    }

    // No refresh token at all (e.g. user opened a deep link in a new
    // tab where localStorage was cleared) — go straight to /login
    // instead of showing a confusing 401 banner on the page.
    if (status === 401 && !refresh && !original.__isRetry && !handshake && !isSignedOut()) {
      clearLocalAuth();
      redirectToLogin();
    }

    // 403 (Forbidden) — the user is authenticated but the backend has
    // refused this resource.
    //
    // We DELIBERATELY only redirect to the friendly AccessDenied page
    // for *navigational* GETs — i.e. the user just landed on a page
    // they can't open.  For unsafe verbs (POST/PUT/PATCH/DELETE) the
    // user is in the middle of a form submission and we leave the
    // 403 response on the page so the caller's ``<ErrorBox/>`` can
    // render the actual reason inline (CSRF failure, missing
    // permission, "Only super-admins may …", per-field validation
    // forbidden, …).
    //
    // Why the change matters: a stale-or-missing JWT causes session
    // auth to take over on the next POST, and any CSRF-token
    // mismatch then comes back as a 403.  The previous unconditional
    // redirect turned every CSRF / auth hiccup during a save into a
    // misleading "/access-denied?reason=unauthorized" bounce — most
    // notably when the super-admin tried to create a user at
    // ``/super-admin/users/new``.  Form submissions now stay on the
    // page and surface the precise backend message.
    //
    // ``original.silent`` (set by polling callers) still opts out
    // entirely so a background poll can never yank the user off-page.
    if (status === 403 && !original.silent) {
      const method = (original.method || 'get').toLowerCase();
      const isNavigationalGet = method === 'get' || method === 'head';
      if (isNavigationalGet) {
        redirectToAccessDenied('unauthorized');
      }
    }


    if (shouldToast(error)) {

      // Always log to the console as well — useful when devtools are open
      // even if the toast was dismissed.
      // eslint-disable-next-line no-console
      console.error('[api]', original.method?.toUpperCase(), original.url, error);
      notify.fromAxios(error, {
        title: status ? `API error ${status}` : 'API error',
      });
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Thin domain wrappers — every page imports from here, never axios directly.
// ---------------------------------------------------------------------------
export const AuthAPI = {
  login: (email, password, extras = {}) =>
    api.post(API.ENDPOINTS.LOGIN, {
      email: canonicalizeLoginIdentifier(email),
      password,
      ...extras,
    }).then((r) => r.data),
  publicSignup: (payload) =>
    api.post(API.ENDPOINTS.PUBLIC_SIGNUP, payload, { silent: true }).then((r) => r.data),
  requestPasswordReset: (email) =>
    api
      .post(
        API.ENDPOINTS.PASSWORD_RESET,
        { email: String(email || '').trim().toLowerCase() },
        { silent: true },
      )
      .then((r) => r.data),
  confirmPasswordReset: (uid, token, newPassword) =>
    api
      .post(
        API.ENDPOINTS.PASSWORD_RESET_CONFIRM,
        { uid, token, new_password: newPassword },
        { silent: true },
      )
      .then((r) => r.data),
  obtainToken: (username, password, extras = {}) =>
    api.post(API.ENDPOINTS.TOKEN, {
      email: canonicalizeLoginIdentifier(username),
      password,
      ...extras,
    }).then((r) => r.data),
  me: () => api.get(API.ENDPOINTS.ME, { silent: true }).then((r) => r.data),
  logout: () => api.post(API.ENDPOINTS.LOGOUT, {}, { silent: true }).then((r) => r.data).catch(() => null),
  blacklistRefresh: (refresh) =>
    api
      .post(API.ENDPOINTS.TOKEN_BLACKLIST, { refresh }, { silent: true })
      .then((r) => r.data)
      .catch(() => null),
};

// ---------------------------------------------------------------------------
// Profile — self-service "edit my own user" endpoints.
//
// Backed by ``UserViewSet.update_me`` and ``UserViewSet.set_my_password``
// in ``super_admin/api_views.py``.  Deliberately separate from
// ``SuperAdminAPI.users`` (the admin-side CRUD) because:
//
//   * the admin endpoint accepts every model field, including
//     privilege flags — letting the regular user PATCH that endpoint
//     against their own row would be a privilege-escalation hole;
//   * the self-service endpoint whitelists only safe identity fields
//     (name, email, phone, profile_image, address) on the server side.
//
// ``update`` accepts EITHER:
//   * a plain object — sent as ``application/json``;
//   * a ``FormData`` instance — sent as ``multipart/form-data`` so the
//     ``profile_image`` <input type="file"> can be uploaded in the
//     same request.  The page builds the FormData by appending each
//     non-empty field plus the optional file blob.
// ---------------------------------------------------------------------------
export const ProfileAPI = {
  /** Fetch the current user's full /me/ payload. */
  retrieve: () => api.get(API.ENDPOINTS.ME).then((r) => r.data),

  /** Update name / email / phone / address / profile picture. */
  update: (payload) => {
    if (payload instanceof FormData) {
      return api
        .patch(API.ENDPOINTS.ME_UPDATE, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    }
    return api.patch(API.ENDPOINTS.ME_UPDATE, payload).then((r) => r.data);
  },

  /**
   * Change the current user's password.
   *
   * The backend requires the caller's CURRENT password (defence
   * against an idle / unattended browser silently swapping the
   * password) — so the page collects it on the same form.
   */
  setPassword: (currentPassword, newPassword) =>
    api
      .post(API.ENDPOINTS.ME_SET_PWD, {
        current_password: currentPassword,
        new_password: newPassword,
      })
      .then((r) => r.data),
};


export const TasksAPI = {
  list: (params = {}) =>
    api.get(API.ENDPOINTS.TASKS, { params }).then((r) => r.data),
  summary: (params = {}) =>
    api.get(API.ENDPOINTS.TASK_SUMMARY, { params }).then((r) => r.data),
  retrieve: (id) =>
    api.get(API.ENDPOINTS.TASK_DETAIL(id)).then((r) => r.data),
  create: (payload) =>
    api.post(API.ENDPOINTS.TASKS, payload).then((r) => r.data),
  update: (id, payload) =>
    api.patch(API.ENDPOINTS.TASK_DETAIL(id), payload).then((r) => r.data),
  remove: (id) =>
    api.delete(API.ENDPOINTS.TASK_DETAIL(id)).then((r) => r.data),
  setStatus: (id, status) =>
    api.post(API.ENDPOINTS.TASK_STATUS(id), { status }).then((r) => r.data),
  comments: (taskId) =>
    api.get(API.ENDPOINTS.TASK_COMMENTS, { params: { task: taskId } }).then((r) => r.data),
  addComment: (taskId, comment) =>
    api.post(API.ENDPOINTS.TASK_COMMENTS, { task: taskId, comment }).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Routine tasks (templates the cron worker expands into Task rows).
//
// The legacy ``cron_repeat_time`` field is read-only on the backend
// (derived from the structured ``cron_*`` fields).  The form sends the
// structured fields directly — see ``RoutineTaskForm.jsx``.
// ---------------------------------------------------------------------------
export const RoutineTasksAPI = {
  list:     (params = {}) =>
    api.get(API.ENDPOINTS.ROUTINE_TASKS, { params }).then((r) => r.data),
  retrieve: (id) =>
    api.get(API.ENDPOINTS.ROUTINE_TASK_DETAIL(id)).then((r) => r.data),
  create:   (payload) =>
    api.post(API.ENDPOINTS.ROUTINE_TASKS, payload).then((r) => r.data),
  update:   (id, payload) =>
    api.patch(API.ENDPOINTS.ROUTINE_TASK_DETAIL(id), payload).then((r) => r.data),
  remove:   (id) =>
    api.delete(API.ENDPOINTS.ROUTINE_TASK_DETAIL(id)).then((r) => r.data),
  // Manual bypass of the daily cron for this routine. Automatic
  // assignment is ``assign_task_to_user`` at midnight; this exists so
  // an operator can still give the assignee today's task if that job
  // has not run (or failed).
  runNow:   (id) =>
    api.post(API.ENDPOINTS.ROUTINE_TASK_RUN_NOW(id)).then((r) => r.data),

  // ---- Excel round-trip --------------------------------------------------
  // ``exportXlsx`` and ``downloadTemplate`` return the raw axios
  // response (not just ``r.data``) because the caller needs both the
  // Blob payload AND the ``Content-Disposition`` header to derive a
  // filename.  ``responseType: 'blob'`` is mandatory — without it
  // axios tries to JSON-decode the binary stream.
  exportXlsx: (params = {}) =>
    api.get(API.ENDPOINTS.ROUTINE_TASK_EXPORT, {
      params,
      responseType: 'blob',
    }),
  downloadTemplate: () =>
    api.get(API.ENDPOINTS.ROUTINE_TASK_TEMPLATE, {
      responseType: 'blob',
    }),
  // ``importXlsx`` accepts a raw ``File`` (from ``<input type="file">``)
  // and posts it as ``multipart/form-data``.  The server replies with
  // ``{ created, errors, error_details, headers }`` which the import
  // page renders as a per-row status report.
  importXlsx: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post(API.ENDPOINTS.ROUTINE_TASK_IMPORT, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // The endpoint returns 400 with a ``{detail}`` body when no
        // rows could be imported (e.g. malformed file).  We want to
        // surface that message in-page rather than as a global toast.
        silent: true,
      })
      .then((r) => r.data);
  },
};

export const ComplaintsAPI = {

  list: (params = {}) =>
    api.get(API.ENDPOINTS.COMPLAINTS, { params }).then((r) => r.data),
  summary: (params = {}) =>
    api.get(API.ENDPOINTS.COMPLAINT_SUMMARY, { params }).then((r) => r.data),
  retrieve: (id) =>
    api.get(API.ENDPOINTS.COMPLAINT_DETAIL(id)).then((r) => r.data),
  create: (payload) => {
    // Allow file uploads transparently.
    if (payload instanceof FormData) {
      return api
        .post(API.ENDPOINTS.COMPLAINTS, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    }
    return api.post(API.ENDPOINTS.COMPLAINTS, payload).then((r) => r.data);
  },
  update: (id, payload) =>
    api.patch(API.ENDPOINTS.COMPLAINT_DETAIL(id), payload).then((r) => r.data),
  remove: (id) =>
    api.delete(API.ENDPOINTS.COMPLAINT_DETAIL(id)).then((r) => r.data),
  setStatus: (id, status) =>
    api.post(API.ENDPOINTS.COMPLAINT_STATUS(id), { status }).then((r) => r.data),
  reassign: (id, userId) =>
    api.post(API.ENDPOINTS.COMPLAINT_REASSIGN(id), { user_id: userId }).then((r) => r.data),
};

export const UsersAPI = {
  list: (params = {}, config = {}) =>
    api.get(API.ENDPOINTS.USERS, { params, ...config }).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Notifications — the aggregated "bell" feed rendered in the AppShell top
// bar.  Backed by ``NotificationInboxView`` (see
// ``notification/api_views.py``), which rolls Tasks / Routine Tasks /
// Complaints notifications for the current user into one payload:
//
//     { generated_at, total, groups: [ { key, label, icon, count, items } ] }
//
// ``inbox`` is polled on an interval by ``NotificationBell`` — the call
// is marked ``silent`` so a transient backend hiccup during background
// polling never yanks the user off-page or spams a global error toast.
// ---------------------------------------------------------------------------
export const NotificationsAPI = {
  inbox: () =>
    api
      .get(API.ENDPOINTS.NOTIFICATION_INBOX, { silent: true })
      .then((r) => r.data),
};


// ---------------------------------------------------------------------------
// Generic CRUD factory — every business / super-admin resource is just a
// plain DRF ModelViewSet, so we can build a uniform wrapper from a list URL
// and a detail-URL builder.  Extra wrappers below add the few one-off
// endpoints (set-password, etc.).
// ---------------------------------------------------------------------------
export function makeCrud(listUrl, detailUrl) {
  const detail = typeof detailUrl === 'function'
    ? detailUrl
    : (id) => `${listUrl}${id}/`;
  return {
    list:     (params = {}) => api.get(listUrl, { params }).then((r) => r.data),
    retrieve: (id, params = {}) => api.get(detail(id), { params }).then((r) => r.data),
    create:   (payload) => {
      if (payload instanceof FormData) {
        return api
          .post(listUrl, payload, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then((r) => r.data);
      }
      return api.post(listUrl, payload).then((r) => r.data);
    },
    update:   (id, payload, params = {}) => {
      if (payload instanceof FormData) {
        return api
          .patch(detail(id), payload, { headers: { 'Content-Type': 'multipart/form-data' }, params })
          .then((r) => r.data);
      }
      return api.patch(detail(id), payload, { params }).then((r) => r.data);
    },
    remove:   (id, params = {}) => api.delete(detail(id), { params }).then((r) => r.data),
  };
}

// --- Business / Organisation Admin -----------------------------------------
//
// ``defaults`` is the system-defaults inspector / seeder used by the
// SPA's Business → Default tab.  It is NOT a CRUD resource — it
// lives next to the rest of the BusinessAPI surface so callers only
// have to import one symbol.  Backed by
// ``organisational_admin/api_defaults.py``.
export const BusinessDefaultsAPI = {
  status: () =>
    api.get(API.ENDPOINTS.BUSINESS_DEFAULTS).then((r) => r.data),
  seed:   (key = 'all') =>
    api.post(API.ENDPOINTS.BUSINESS_DEFAULTS_SEED, { key }).then((r) => r.data),
};

// Organisation-level integration settings (Business → Integrations).
// Backed by ``organisational_admin/api_integrations.py``.
export const BusinessIntegrationsAPI = {
  googleSheets: {
    get: () =>
      api.get(API.ENDPOINTS.BUSINESS_GOOGLE_SHEETS).then((r) => r.data),
    update: (body) =>
      api.patch(API.ENDPOINTS.BUSINESS_GOOGLE_SHEETS, body).then((r) => r.data),
  },
};

export const BusinessPublicComplaintsAPI = {
  get: () =>
    api.get(API.ENDPOINTS.BUSINESS_PUBLIC_COMPLAINT_ACCESS).then((r) => r.data),
  update: (body) =>
    api.patch(API.ENDPOINTS.BUSINESS_PUBLIC_COMPLAINT_ACCESS, body).then((r) => r.data),
};

export const BusinessComplaintSerialAPI = {
  get: () =>
    api.get(API.ENDPOINTS.BUSINESS_COMPLAINT_SERIAL).then((r) => r.data),
  update: (body) =>
    api.patch(API.ENDPOINTS.BUSINESS_COMPLAINT_SERIAL, body).then((r) => r.data),
};

export const BusinessTimezoneAPI = {
  get: () =>
    api.get(API.ENDPOINTS.BUSINESS_TIMEZONE).then((r) => r.data),
  update: (body) =>
    api.patch(API.ENDPOINTS.BUSINESS_TIMEZONE, body).then((r) => r.data),
};

// Organisation schema backup. Download returns the raw axios response
// (blob + Content-Disposition). The server chooses the schema from the
// request host; callers do not send a schema name.
const SCHEMA_BACKUP_TIMEOUT_MS = 300_000;

export const BusinessSchemaBackupAPI = {
  status: () =>
    api.get(API.ENDPOINTS.BUSINESS_SCHEMA_BACKUP).then((r) => r.data),
  download: () =>
    api.get(API.ENDPOINTS.BUSINESS_SCHEMA_BACKUP_DOWNLOAD, {
      responseType: 'blob',
      timeout: SCHEMA_BACKUP_TIMEOUT_MS,
    }),
  restore: (file, schemaName) => {
    const body = new FormData();
    body.append('file', file);
    body.append('confirm', schemaName);
    body.append('replace', 'true');
    return api
      .post(API.ENDPOINTS.BUSINESS_SCHEMA_BACKUP_RESTORE, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: SCHEMA_BACKUP_TIMEOUT_MS,
      })
      .then((r) => r.data);
  },
};

// Name and logo members see in the mobile app after sign-in. ``update``
// takes FormData (app_name, optional logo file, optional remove_logo)
// because the logo is an image upload.
export const BusinessMobileBrandingAPI = {
  get: () =>
    api.get(API.ENDPOINTS.BUSINESS_MOBILE_BRANDING).then((r) => r.data),
  update: (body) =>
    api
      .patch(API.ENDPOINTS.BUSINESS_MOBILE_BRANDING, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data),
};

// --- Per-user module access (Business → Access Control) --------------------
//
// Backed by ``UserModuleAccessViewSet`` (see
// ``super_admin/api_views.py``).  ``matrix`` returns the full
// {modules, levels, users[]} grid the Access Control screen renders in
// one round-trip; ``bulkSet`` replaces a single user's entire
// per-module access map atomically.  Both are org-admin only — the
// backend rejects non-admins with a 403 (which the interceptor turns
// into the friendly AccessDenied page).
export const ModuleAccessAPI = {
  matrix: () =>
    api.get(API.ENDPOINTS.MODULE_ACCESS_MATRIX).then((r) => r.data),
  bulkSet: (userId, access) =>
    api
      .post(API.ENDPOINTS.MODULE_ACCESS_BULK_SET, { user: userId, access })
      .then((r) => r.data),
  // Copy one user's whole module-access map onto another user in a
  // single atomic call.  Backed by ``UserModuleAccessViewSet.copy`` —
  // returns ``{user, access}`` (the target's refreshed map) so the SPA
  // can update its local state without re-fetching the whole matrix.
  copy: (sourceId, targetId) =>
    api
      .post(API.ENDPOINTS.MODULE_ACCESS_COPY, { source: sourceId, target: targetId })
      .then((r) => r.data),
};

// --- Pharma SFA per-screen CRUD access (Business → Access Control) ---------
//
// Pharma SFA is gated at a finer grain than the coarse Admin/User/View/
// None dropdown every other module uses: each screen carries its own
// CRUD checkboxes.  Backed by ``PharmaScreenAccessViewSet`` (see
// ``super_admin/pharma_access_views.py``), all org-admin only.
//
//   * ``catalog``  → the static tab→screen→actions structure the grid
//     renders its rows / columns from (matches
//     ``super_admin/pharma_screens.py``).
//   * ``matrix``   → every user's per-screen CRUD grid in one round-trip.
//   * ``bulkSet``  → replace ONE user's whole Pharma grid atomically.
//     ``access`` is ``{screen_key: {create, read, update, delete}}``.
//   * ``copy``     → mirror one user's Pharma grid onto another.
export const PharmaAccessAPI = {
  catalog: () =>
    api.get(API.ENDPOINTS.PHARMA_ACCESS_CATALOG).then((r) => r.data),
  matrix: () =>
    api.get(API.ENDPOINTS.PHARMA_ACCESS_MATRIX).then((r) => r.data),
  bulkSet: (userId, access) =>
    api
      .post(API.ENDPOINTS.PHARMA_ACCESS_BULK_SET, { user: userId, screens: access })
      .then((r) => r.data),

  copy: (sourceId, targetId) =>
    api
      .post(API.ENDPOINTS.PHARMA_ACCESS_COPY, { source: sourceId, target: targetId })
      .then((r) => r.data),
};

// --- Permission user groups (Business → Access Control) ----------------------
export const PermissionGroupAPI = {
  list: (params) =>
    api.get(API.ENDPOINTS.PERMISSION_USER_GROUPS, { params }).then((r) => r.data),
  create: (data) =>
    api.post(API.ENDPOINTS.PERMISSION_USER_GROUPS, data).then((r) => r.data),
  update: (id, data) =>
    api.patch(API.ENDPOINTS.PERMISSION_USER_GROUP_DETAIL(id), data).then((r) => r.data),
  remove: (id) =>
    api.delete(API.ENDPOINTS.PERMISSION_USER_GROUP_DETAIL(id)),
  matrix: () =>
    api.get(API.ENDPOINTS.PERMISSION_USER_GROUPS_MATRIX).then((r) => r.data),
  setMembers: (groupId, userIds) =>
    api
      .post(API.ENDPOINTS.PERMISSION_USER_GROUP_MEMBERS(groupId), { users: userIds })
      .then((r) => r.data),
  bulkSetModules: (groupId, access) =>
    api
      .post(API.ENDPOINTS.PERMISSION_USER_GROUP_MODULE_BULK(groupId), { access })
      .then((r) => r.data),
  bulkSetPharma: (groupId, screens) =>
    api
      .post(API.ENDPOINTS.PERMISSION_USER_GROUP_PHARMA_BULK(groupId), { screens })
      .then((r) => r.data),
};

// --- Per-module screen CRUD access (Business → Access Control) -----------
//
// Generalises Pharma's per-screen grid to every business module.
// Backed by ``ModuleScreenAccessViewSet`` (see
// ``super_admin/module_screen_access_views.py``), all org-admin only.
export const ScreenAccessAPI = {
  modules: () =>
    api.get(API.ENDPOINTS.SCREEN_ACCESS_MODULES).then((r) => r.data),
  catalog: (module) =>
    api.get(API.ENDPOINTS.SCREEN_ACCESS_CATALOG, { params: { module } }).then((r) => r.data),
  matrix: (module) =>
    api.get(API.ENDPOINTS.SCREEN_ACCESS_MATRIX, { params: { module } }).then((r) => r.data),
  bulkSet: (module, userId, screens) =>
    api
      .post(API.ENDPOINTS.SCREEN_ACCESS_BULK_SET, { module, user: userId, screens })
      .then((r) => r.data),
  copy: (module, sourceId, targetId) =>
    api
      .post(API.ENDPOINTS.SCREEN_ACCESS_COPY, { module, source: sourceId, target: targetId })
      .then((r) => r.data),
};

export const BusinessAPI = {
  divisions:              makeCrud(API.ENDPOINTS.DIVISIONS,        API.ENDPOINTS.DIVISION_DETAIL),
  subDivisions:           makeCrud(API.ENDPOINTS.SUB_DIVISIONS,    API.ENDPOINTS.SUB_DIVISION_DETAIL),



  groups:                 makeCrud(API.ENDPOINTS.GROUPS,           API.ENDPOINTS.GROUP_DETAIL),
  hrManagements:          makeCrud(API.ENDPOINTS.HR_MANAGEMENTS,   API.ENDPOINTS.HR_MANAGEMENT_DETAIL),
  noticeTemplates:        makeCrud(API.ENDPOINTS.NOTICE_TEMPLATES, API.ENDPOINTS.NOTICE_TEMPLATE_DETAIL),
  projects:               makeCrud(API.ENDPOINTS.PROJECTS,         API.ENDPOINTS.PROJECT_DETAIL),
  departments:            makeCrud(API.ENDPOINTS.DEPARTMENTS,      API.ENDPOINTS.DEPARTMENT_DETAIL),
  depots:                 makeCrud(API.ENDPOINTS.DEPOTS,           API.ENDPOINTS.DEPOT_DETAIL),
  products:               makeCrud(API.ENDPOINTS.PRODUCTS,         API.ENDPOINTS.PRODUCT_DETAIL),
  publicComplaintAdmins:  makeCrud(API.ENDPOINTS.PUBLIC_COMPLAINT_ADMINS, API.ENDPOINTS.PUBLIC_COMPLAINT_ADMIN_DETAIL),
};

// --- Super Admin -----------------------------------------------------------
export const SuperAdminAPI = {
  organisations: {
    ...makeCrud(API.ENDPOINTS.ORGANISATIONS, API.ENDPOINTS.ORGANISATION_DETAIL),
    lockDetails: (id, pin, pinConfirmation) =>
      api.post(API.ENDPOINTS.ORGANISATION_LOCK_DETAILS(id), {
        pin,
        pin_confirmation: pinConfirmation,
      }).then((r) => r.data),
    unlockDetails: (id, pin) =>
      api.post(API.ENDPOINTS.ORGANISATION_UNLOCK_DETAILS(id), { pin }).then((r) => r.data),
  },
  users:           makeCrud(API.ENDPOINTS.USERS,            API.ENDPOINTS.USER_DETAIL),
  roles:           makeCrud(API.ENDPOINTS.ROLES,            API.ENDPOINTS.ROLE_DETAIL),
  permissions:     makeCrud(API.ENDPOINTS.PERMISSIONS,      API.ENDPOINTS.PERMISSION_DETAIL),
  hierarchies:     makeCrud(API.ENDPOINTS.HIERARCHIES,      API.ENDPOINTS.HIERARCHY_DETAIL),
  rolePermissions: makeCrud(API.ENDPOINTS.ROLE_PERMISSIONS, API.ENDPOINTS.ROLE_PERMISSION_DETAIL),
  modules:         makeCrud(API.ENDPOINTS.MODULES,          API.ENDPOINTS.MODULE_DETAIL),
  controls:        makeCrud(API.ENDPOINTS.CONTROLS,         API.ENDPOINTS.CONTROL_DETAIL),
  setUserPassword: (id, password, params = {}) =>
    api.post(API.ENDPOINTS.USER_SET_PWD(id), { password }, { params }).then((r) => r.data),

  /**
   * Server-side autocomplete search for the Create-User organisation
   * dropdown.
   *
   * Backed by ``OrganisationViewSet.lookup`` (see
   * ``super_admin/api_views.py``).  Always reads from the public
   * schema (the canonical tenant registry) regardless of which host
   * the SPA is on, and is paginated to 10 results per request.
   *
   * Pass ``signal`` (an ``AbortController.signal``) so the autocomplete
   * can cancel an in-flight request when the user keeps typing — the
   * old result would otherwise be stale by the time it lands.
   *
   * Returns the raw DRF page object: ``{ count, next, previous, results }``.
   */
  organisationsLookup: ({ search = '', signal } = {}) =>
    api
      .get(API.ENDPOINTS.ORGANISATION_LOOKUP, {
        params: search ? { search } : {},
        signal,
        // Don't surface the dropdown's internal errors as global toasts —
        // the autocomplete renders its own inline message.
        silent: true,
      })
      .then((r) => r.data),

  /**
   * Site-wide login-security policy (superuser-only).
   *
   * GET/PUT ``/api/super_admin/login-security/``. Organisation admins
   * receive 403. Public client flags stay on the unauthenticated
   * ``LOGIN_SECURITY_CLIENT_CONFIG`` endpoint used by LoginPage.
   */
  loginSecurity: {
    get: () =>
      api.get(API.ENDPOINTS.LOGIN_SECURITY_CONFIG).then((r) => r.data),
    save: (payload) =>
      api.put(API.ENDPOINTS.LOGIN_SECURITY_CONFIG, payload).then((r) => r.data),
  },

  successYatraMove: {
    workspaces: () =>
      api.get(API.ENDPOINTS.SUCCESSYATRA_WORKSPACES).then((r) => r.data),
    members: (workspace, q) =>
      api.get(API.ENDPOINTS.SUCCESSYATRA_MEMBERS, { params: { workspace, q } }).then((r) => r.data),
    companies: (workspace) =>
      api.get(API.ENDPOINTS.SUCCESSYATRA_COMPANIES, { params: { workspace } }).then((r) => r.data),
    move: (payload) =>
      api.post(API.ENDPOINTS.SUCCESSYATRA_MOVE, payload).then((r) => r.data),
  },

  moduleDefaults: {
    get: () =>
      api.get(API.ENDPOINTS.MODULE_DEFAULTS).then((r) => r.data),
    save: (modules) =>
      api.put(API.ENDPOINTS.MODULE_DEFAULTS, { modules }).then((r) => r.data),
  },

  organisationModules: {
    get: (id) =>
      api.get(API.ENDPOINTS.ORGANISATION_MODULES(id)).then((r) => r.data),
    save: (id, modules) =>
      api.put(API.ENDPOINTS.ORGANISATION_MODULES(id), { modules }).then((r) => r.data),
  },

  /**
   * Superuser-only database viewer / editor.
   *
   * Organisation admins receive 403. Query arrays (filters) are serialised
   * as repeated keys so Django's ``getlist`` can zip them back together.
   */
  dbViewer: {
    home: (params = {}) =>
      api.get(API.ENDPOINTS.DB_VIEWER + dbViewerQuery(params)).then((r) => r.data),
    schema: (alias, schema, params = {}) =>
      api
        .get(API.ENDPOINTS.DB_VIEWER_SCHEMA(alias, schema) + dbViewerQuery(params))
        .then((r) => r.data),
    table: (alias, schema, table, params = {}) =>
      api
        .get(API.ENDPOINTS.DB_VIEWER_TABLE(alias, schema, table) + dbViewerQuery(params))
        .then((r) => r.data),
    updateCell: (alias, schema, table, payload) =>
      api
        .post(API.ENDPOINTS.DB_VIEWER_UPDATE_CELL(alias, schema, table), payload)
        .then((r) => r.data),
    addRowForm: (alias, schema, table) =>
      api.get(API.ENDPOINTS.DB_VIEWER_ADD_ROW(alias, schema, table)).then((r) => r.data),
    addRow: (alias, schema, table, payload) =>
      api
        .post(API.ENDPOINTS.DB_VIEWER_ADD_ROW(alias, schema, table), payload)
        .then((r) => r.data),
    deleteRow: (alias, schema, table, payload) =>
      api
        .post(API.ENDPOINTS.DB_VIEWER_DELETE_ROW(alias, schema, table), payload)
        .then((r) => r.data),
  },
};

function dbViewerQuery(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => usp.append(key, item));
    } else if (value !== undefined && value !== null && value !== '') {
      usp.append(key, value);
    }
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Chat — REST for conversation list, contacts, and message history.
// Live send/receive uses ``src/lib/chatSocket.js`` (vanilla WebSocket).
// ``messages({ conversation, since })`` is for initial load + reconnect gap-fill.
// ---------------------------------------------------------------------------
export const ChatAPI = {
  // Conversations -----------------------------------------------------------
  conversations: () =>
    api.get(API.ENDPOINTS.CHAT_CONVERSATIONS).then((r) => r.data),
  conversation: (id) =>
    api.get(API.ENDPOINTS.CHAT_CONVERSATION_DETAIL(id)).then((r) => r.data),
  /** Start (or fetch the existing) 1-on-1 chat with `userId`. */
  openDirect: (userId) =>
    api
      .post(API.ENDPOINTS.CHAT_CONVERSATIONS, { user_id: userId })
      .then((r) => r.data),
  /** Create a brand-new group chat. */
  createGroup: (userIds, title = '') =>
    api
      .post(API.ENDPOINTS.CHAT_CONVERSATIONS, { user_ids: userIds, title })
      .then((r) => r.data),
  markRead: (conversationId) =>
    api.post(API.ENDPOINTS.CHAT_MARK_READ(conversationId)).then((r) => r.data),

  // Messages ----------------------------------------------------------------
  /**
   * Fetch messages for a conversation.  Pass `since` (the last seen
   * message id) to get only the newer ones — used on first load and
   * after a WebSocket reconnect.
   */
  messages: (conversationId, { since } = {}) => {
    const params = { conversation: conversationId };
    if (since) params.since = since;
    return api.get(API.ENDPOINTS.CHAT_MESSAGES, { params }).then((r) => r.data);
  },
  send: (conversationId, body) =>
    api
      .post(API.ENDPOINTS.CHAT_MESSAGES, {
        conversation: conversationId,
        body,
      })
      .then((r) => r.data),

  // Contacts ----------------------------------------------------------------
  contacts: () =>
    api.get(API.ENDPOINTS.CHAT_CONTACTS).then((r) => r.data),
  /** View-only identity card for a colleague (same organisation). */
  contact: (id) =>
    api.get(API.ENDPOINTS.CHAT_CONTACT_DETAIL(id)).then((r) => r.data),
};

export default api;
